import React, { useState } from "react";
import { calculateGestationalAge, calculateGAFromEDD, formatGplad, parseGplad } from "../utils/EpisodeMeta";

interface EpisodeField {
  key: string;
  label: string;
  type: string;
}

interface Episode {
  id: number;
  title?: string;
  meta?: Record<string, any>;
  episodeType?: {
    id: number;
    name: string;
    fields: EpisodeField[];
  };
  assignedUser?: {
    id: number;
    name?: string;
    firstName?: string;
    lastName?: string;
  } | null;
}

interface Props {
  episode: Episode;
}

const EpisodeContextBanner: React.FC<Props> = ({ episode }) => {
  const [expanded, setExpanded] = useState(false);

  const meta = episode.meta ?? {};
  const fields: EpisodeField[] = episode.episodeType?.fields ?? [];
  const typeName = episode.episodeType?.name ?? "Episode";

  const renderFieldValue = (field: EpisodeField) => {
    const raw = meta[field.key];
    if (raw === undefined || raw === null || raw === "") return null;

    if (field.type === "lmp") {
      const ga = calculateGestationalAge(raw);
      return (
        <span>
          <span className="text-gray-700">{raw}</span>
          {ga && (
            <span className="ml-1.5 inline-flex items-center rounded-full bg-rose-50 px-2 py-0.5 text-xs font-medium text-rose-700 ring-1 ring-inset ring-rose-100">
              {ga.label} GA
            </span>
          )}
        </span>
      );
    }

    if (field.type === "gplad") {
      const val = parseGplad(raw);
      const formatted = formatGplad(val);
      if (!formatted) return null;
      return <span className="text-gray-700 font-mono text-xs">{formatted}</span>;
    }

    if (field.type === "boolean") {
      return (
        <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${
          raw ? "bg-green-50 text-green-700 ring-green-100" : "bg-gray-50 text-gray-500 ring-gray-200"
        }`}>
          {raw ? "Yes" : "No"}
        </span>
      );
    }

    if (field.type === "edd") {
      const eddObj = typeof raw === "object" && raw !== null ? raw : {};
      const active: string = eddObj.active ?? "lmp";
      const activeDate: string = active === "usg" ? (eddObj.usg_edd ?? "") : (eddObj.lmp_edd ?? "");
      if (!activeDate) return null;
      const ga = calculateGAFromEDD(activeDate);
      return (
        <span className="flex items-center gap-1.5">
          <span className="text-gray-700">{activeDate}</span>
          {active === "usg" && (
            <span className="inline-flex items-center rounded-full bg-blue-50 px-1.5 py-0.5 text-[10px] font-semibold text-blue-700 ring-1 ring-inset ring-blue-100">USG</span>
          )}
          {ga && (
            <span className="inline-flex items-center rounded-full bg-rose-50 px-1.5 py-0.5 text-[10px] font-semibold text-rose-700 ring-1 ring-inset ring-rose-100">
              {ga.label} GA
            </span>
          )}
        </span>
      );
    }

    if (field.type === "history_table") {
      if (!Array.isArray(raw) || raw.length === 0) return null;
      return (
        <span className="text-gray-700 text-xs">{raw.length} previous {raw.length === 1 ? "pregnancy" : "pregnancies"}</span>
      );
    }

    return <span className="text-gray-700">{String(raw)}</span>;
  };

  // Smart summary: pick fields with values to show in collapsed row (skip history_table)
  const summaryFields = fields
    .filter((f) => {
      if (f.type === "history_table") return false;
      const v = meta[f.key];
      if (v === undefined || v === null || v === "") return false;
      if (f.type === "edd") {
        const obj = typeof v === "object" ? v : {};
        return !!(obj.lmp_edd || obj.usg_edd);
      }
      return true;
    })
    .slice(0, 4);

  // For lmp, always show GA even in summary
  const hasData = summaryFields.length > 0 || Object.keys(meta).length > 0;
  if (!hasData && !episode.title) return null;

  return (
    <div className="bg-indigo-50 border-b border-indigo-100">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center justify-between px-4 py-2 text-left"
      >
        <div className="flex items-center gap-2 min-w-0">
          <span className="shrink-0 inline-flex items-center rounded-md bg-indigo-100 px-2 py-0.5 text-xs font-semibold text-indigo-700 ring-1 ring-inset ring-indigo-200">
            {typeName}
          </span>
          {episode.assignedUser && (
            <span className="shrink-0 flex items-center gap-1 text-xs text-gray-500">
              <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5 text-gray-400" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M17.982 18.725A7.488 7.488 0 0 0 12 15.75a7.488 7.488 0 0 0-5.982 2.975m11.963 0a9 9 0 1 0-11.963 0m11.963 0A8.966 8.966 0 0 1 12 21a8.966 8.966 0 0 1-5.982-2.275M15 9.75a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
              </svg>
              {[episode.assignedUser.firstName, episode.assignedUser.lastName].filter(Boolean).join(" ") || episode.assignedUser.name}
            </span>
          )}
          {!expanded && summaryFields.length > 0 && (
            <span className="flex items-center gap-3 text-xs text-gray-600 truncate">
              {summaryFields.map((f) => {
                const raw = meta[f.key];
                if (f.type === "lmp") {
                  const ga = calculateGestationalAge(raw);
                  return (
                    <span key={f.key} className="flex items-center gap-1">
                      <span className="text-gray-400">{f.label}:</span>
                      <span>{raw}</span>
                      {ga && (
                        <span className="inline-flex items-center rounded-full bg-rose-50 px-1.5 py-0.5 text-[10px] font-semibold text-rose-700 ring-1 ring-inset ring-rose-100">
                          {ga.label} GA
                        </span>
                      )}
                    </span>
                  );
                }
                if (f.type === "gplad") {
                  const formatted = formatGplad(parseGplad(raw));
                  if (!formatted) return null;
                  return (
                    <span key={f.key} className="flex items-center gap-1">
                      <span className="text-gray-400">{f.label}:</span>
                      <span className="font-mono">{formatted}</span>
                    </span>
                  );
                }
                if (f.type === "edd") {
                  const eddObj = typeof raw === "object" && raw !== null ? raw : {};
                  const active: string = eddObj.active ?? "lmp";
                  const activeDate: string = active === "usg" ? (eddObj.usg_edd ?? "") : (eddObj.lmp_edd ?? "");
                  if (!activeDate) return null;
                  const ga = calculateGAFromEDD(activeDate);
                  return (
                    <span key={f.key} className="flex items-center gap-1">
                      <span className="text-gray-400">{f.label}:</span>
                      <span>{activeDate}</span>
                      {ga && (
                        <span className="inline-flex items-center rounded-full bg-rose-50 px-1.5 py-0.5 text-[10px] font-semibold text-rose-700 ring-1 ring-inset ring-rose-100">
                          {ga.label} GA
                        </span>
                      )}
                    </span>
                  );
                }
                return (
                  <span key={f.key} className="flex items-center gap-1">
                    <span className="text-gray-400">{f.label}:</span>
                    <span>{typeof raw === "boolean" ? (raw ? "Yes" : "No") : String(raw)}</span>
                  </span>
                );
              })}
            </span>
          )}
          {!expanded && summaryFields.length === 0 && (
            <span className="text-xs text-gray-500">
              {episode.title || `Episode #${episode.id}`}
            </span>
          )}
        </div>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className={`w-4 h-4 text-indigo-400 shrink-0 ml-2 transition-transform ${expanded ? "rotate-180" : ""}`}
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth="2"
          stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {expanded && (
        <div className="px-4 pb-3">
          {fields.length === 0 && Object.keys(meta).length === 0 ? (
            <p className="text-xs text-gray-400">No intake data recorded for this episode.</p>
          ) : (
            <dl className="grid grid-cols-2 gap-x-6 gap-y-2 sm:grid-cols-3 lg:grid-cols-4">
              {fields.map((f) => {
                const rendered = renderFieldValue(f);
                if (rendered === null) return null;
                return (
                  <div key={f.key}>
                    <dt className="text-[10px] font-semibold uppercase tracking-wider text-gray-400">{f.label}</dt>
                    <dd className="mt-0.5 text-sm">{rendered}</dd>
                  </div>
                );
              })}
              {/* Render any meta keys not in fields schema */}
              {Object.entries(meta)
                .filter(([k]) => !fields.some((f) => f.key === k))
                .map(([k, v]) => {
                  if (v === null || v === undefined || v === "") return null;
                  return (
                    <div key={k}>
                      <dt className="text-[10px] font-semibold uppercase tracking-wider text-gray-400">{k}</dt>
                      <dd className="mt-0.5 text-sm text-gray-700">{String(v)}</dd>
                    </div>
                  );
                })}
            </dl>
          )}
        </div>
      )}
    </div>
  );
};

export default EpisodeContextBanner;
